import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { ThemeProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/en";
import "dayjs/locale/pl";

import Routes from "web/Layout/Routes";

import useFocusOnRouteChange from "web/hooks/useFocusOnRouteChange";
import useProviderValue from "web/hooks/useProviderValue";

import setDomPurifyAttributes from "web/utils/setDomPurifyAttributes";
import graphQlAsGet from "web/utils/system/query/graphQlAsGet";

import { magentoUrlKey, msURlKey } from "web/constants/graphqlUrls";

import GrowthBookFF from "web/context/GrowthBookFF";
import { AppProvider } from "web/context/app";
import { FeatureFlagsProvider } from "web/context/featureFlags";
import { GoogleProvider } from "web/context/google.context";
import { theme } from "web/theme/theme";

import MobileAppUpdateModal from "../MobileAppUpdateModal";
import RedirectToNativeStore from "../RedirectToNativeStore";
import { getLanguageCode } from "../Translations/LanguageWrapper";

setDomPurifyAttributes();

type GraphqlAsGetCasted = (
  input: RequestInfo | URL,
  init?: RequestInit | undefined
) => Promise<Response>;

const magentoLink = new HttpLink({
  uri: magentoUrlKey,
  fetch: graphQlAsGet as GraphqlAsGetCasted,
});

const msLink = new HttpLink({
  uri: msURlKey,
  fetch: graphQlAsGet as GraphqlAsGetCasted,
});

const apolloClient = new ApolloClient({
  link: ApolloLink.split(
    (operation) => operation.getContext().clientName === "ms",
    msLink,
    magentoLink
  ),
  cache: new InMemoryCache(),
});

const featureFlags = {
  apiUrl: process.env.FEATURE_FLAGS_URL || "",
};

const App = () => {
  const providerValue = useProviderValue();
  const language = getLanguageCode();

  useFocusOnRouteChange();

  return (
    <ThemeProvider theme={theme({ language })}>
      <RedirectToNativeStore>
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={language}
        >
          <ApolloProvider client={apolloClient}>
            <AppProvider value={providerValue}>
              <GrowthBookFF>
                <FeatureFlagsProvider options={featureFlags}>
                  <GoogleProvider>
                    <Routes />
                    <MobileAppUpdateModal />
                  </GoogleProvider>
                </FeatureFlagsProvider>
              </GrowthBookFF>
            </AppProvider>
          </ApolloProvider>
        </LocalizationProvider>
      </RedirectToNativeStore>
    </ThemeProvider>
  );
};

export default App;
